import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { validatePostalCode } from '../../validationUtils';
import { getAddress, getCurrentDate } from '../../utilities';
import '../student/Newstudent.css';
import IBANInput from '../../components/IBANInput.js';

function NewDocent() {
  const { userInfo } = useAuth();
  const role = userInfo?.role || '';

  const [values, setValues] = useState({
    firstname: '',
    lastname: '',
    birthdate: '',
    gender: '',
    datein: getCurrentDate(),
    dateout: '',
    street: '',
    housenr: '',
    postalcode: '',
    city: '',
    iban: '',
    accountowner: '',
    comments: '',
    cv: '',
    email: '',
    mobile: '',
  });

  const [postalcodeValidation, setPostalCodeValidation] = useState({
    valid: true,
    message: ''
  });
  const [emailExists, setEmailExists] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  useEffect(() => {
    if (role !== 'super_admin') return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      })
      .then((res) => {
        const locs = res.data.data || res.data;
        setLocations(Array.isArray(locs) ? locs : []);
      })
      .catch((err) => {
        console.error('Error fetching locations', err);
        setError('Fout bij het ophalen van locaties.');
      });
  }, [role, token]);

  const handleEmailBlur = async () => {
    const email = values.email.trim();

    if (!email) {
      setEmailError('E-mailadres is verplicht.');
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError('Voer een geldig e-mailadres in.');
      return;
    }

    setEmailError('');
    setError('');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-existence`,
        {
          params: { email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEmailExists(response.data.exists);

      if (response.data.exists) {
        setEmailError('Dit e-mailadres is al in gebruik.');
      }
    } catch (err) {
      console.error('Error checking email:', err);
      setError('Fout bij het controleren van het e-mailadres.');
    }
  };

  const handleCheckboxChange = (locId) => {
    setSelectedLocations((prev) =>
      prev.includes(locId)
        ? prev.filter((id) => id !== locId)
        : [...prev, locId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    const email = values.email.trim();

    if (!email) {
      setEmailError('E-mailadres is verplicht.');
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError('Voer een geldig e-mailadres in.');
      return;
    }

    if (emailExists) {
      setEmailError('Dit e-mailadres is al in gebruik.');
      return;
    }

    const payload = {
      ...values,
      email,
      location_ids:
        role === 'super_admin'
          ? selectedLocations
          : [userInfo.user_location_ids[0]],
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/docent/adddocent`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.Status === 'Success') {
        navigate('/docenten');
      } else {
        setError('Er is iets misgegaan bij het opslaan van de docent.');
      }
    } catch (err) {
      console.error(err);

      const rawMessage =
        err.response?.data?.message ||
        err.response?.data?.Error ||
        err.response?.data?.error ||
        '';

      if (
        rawMessage.includes('"email" must be a valid email') ||
        rawMessage.toLowerCase().includes('valid email')
      ) {
        setEmailError('Voer een geldig e-mailadres in.');
      } else if (
        rawMessage.toLowerCase().includes('email already exists') ||
        rawMessage.toLowerCase().includes('already in use')
      ) {
        setEmailError('Dit e-mailadres is al in gebruik.');
      } else {
        setError(rawMessage || 'Er is een fout opgetreden bij het opslaan van de docent.');
      }
    }
  };

  const handleCancel = () => navigate('/alldocenten');

  return (
    <div className="bg-white p-4">
      <h2 className="text-center">Nieuwe Docent</h2>
      <hr className="seperator" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {(role === 'super_admin' && !userInfo?.is_single_user) && (
        <div className="mb-3">
          <label className="d-block">Kies de locatie(s) van deze docent:</label>
          <hr className="seperator" />
          <div className="d-flex flex-wrap">
            {locations.map((loc) => (
              <div key={loc.location_id} className="form-check me-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`loc-${loc.location_id}`}
                  checked={selectedLocations.includes(loc.location_id)}
                  onChange={() => handleCheckboxChange(loc.location_id)}
                />
                <label className="form-check-label" htmlFor={`loc-${loc.location_id}`}>
                  {loc.location_name || loc.name}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <hr className="seperator" />

      <form onSubmit={handleSubmit} className="form-docent">
        <div className="row">
          <div className="col mb-3">
            <label>
              Voornaam<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              required
              className="form-control"
              value={values.firstname}
              onChange={(e) =>
                setValues((v) => ({ ...v, firstname: e.target.value }))
              }
            />
          </div>
          <div className="col mb-3">
            <label>
              Achternaam<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              required
              className="form-control"
              value={values.lastname}
              onChange={(e) =>
                setValues((v) => ({ ...v, lastname: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="row">
          <div className="col mb-3">
            <label>
              Geboortedatum<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="date"
              required
              className="form-control"
              value={values.birthdate}
              onChange={(e) =>
                setValues((v) => ({ ...v, birthdate: e.target.value }))
              }
            />
          </div>
          <div className="col mb-3">
            <label>Geslacht</label>
            <select
              className="form-control"
              value={values.gender}
              onChange={(e) =>
                setValues((v) => ({ ...v, gender: e.target.value }))
              }
            >
              <option value="">Maak een keuze</option>
              <option value="Male">Man</option>
              <option value="Female">Vrouw</option>
              <option value="Other">Anders</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col mb-3">
            <label>Postcode</label>
            <input
              className={`form-control ${postalcodeValidation.valid ? '' : 'is-invalid'}`}
              value={values.postalcode}
              onChange={(e) => {
                setValues((v) => ({ ...v, postalcode: e.target.value }));
                validatePostalCode(e.target.value);
              }}
              onBlur={(e) => {
                if (!validatePostalCode(e.target.value)) {
                  setPostalCodeValidation({
                    valid: false,
                    message: 'Ongeldige postcode'
                  });
                } else {
                  setPostalCodeValidation({ valid: true, message: '' });
                }
              }}
            />
            {!postalcodeValidation.valid && (
              <div className="invalid-feedback">{postalcodeValidation.message}</div>
            )}
          </div>
          <div className="col mb-3">
            <label>Huisnummer</label>
            <input
              className="form-control"
              value={values.housenr}
              onChange={(e) =>
                setValues((v) => ({ ...v, housenr: e.target.value }))
              }
              onBlur={() => getAddress(values, setValues, postalcodeValidation)}
            />
          </div>
        </div>

        <div className="row">
          <div className="col mb-3">
            <label>Straat</label>
            <input readOnly className="form-control" value={values.street} />
          </div>
          <div className="col mb-3">
            <label>Plaats</label>
            <input readOnly className="form-control" value={values.city} />
          </div>
        </div>

        <div className="row">
          <IBANInput
            value={values.iban}
            onChange={(iban) => setValues((v) => ({ ...v, iban }))}
          />
          <div className="col mb-3">
            <label>Rekeninghouder</label>
            <input
              className="form-control"
              value={values.accountowner}
              onChange={(e) =>
                setValues((v) => ({ ...v, accountowner: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="mb-3">
          <label>Opmerkingen</label>
          <textarea
            className="form-control"
            rows="3"
            value={values.comments}
            onChange={(e) =>
              setValues((v) => ({ ...v, comments: e.target.value }))
            }
          />
        </div>

        <div className="mb-3">
          <label>CV (link)</label>
          <input
            className="form-control"
            value={values.cv}
            onChange={(e) =>
              setValues((v) => ({ ...v, cv: e.target.value }))
            }
          />
        </div>

        <div className="row">
          <div className="col mb-3">
            <label>
              Email<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="email"
              required
              className={`form-control ${emailError ? 'is-invalid' : ''}`}
              value={values.email}
              onChange={(e) => {
                const newEmail = e.target.value;
                setValues((v) => ({ ...v, email: newEmail }));
                setEmailExists(false);
                setEmailError('');
                setError('');
              }}
              onBlur={handleEmailBlur}
            />
            {emailError && (
              <div className="invalid-feedback d-block">{emailError}</div>
            )}
          </div>
          <div className="col mb-3">
            <label>Mobiel</label>
            <input
              className="form-control"
              value={values.mobile}
              onChange={(e) =>
                setValues((v) => ({ ...v, mobile: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-6">
            <button
              type="button"
              className="btn btn-secondary w-100"
              onClick={handleCancel}
            >
              Annuleren
            </button>
          </div>
          <div className="col-6">
            <button type="submit" className="btn btn-primary w-100">
              Opslaan
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NewDocent;